<template>
  <TransitionRoot
    appear
    :show="isOpen"
    as="template"
  >
    <Dialog
      as="div"
      class="relative z-50"
      @close="close"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="pointer-events-none fixed inset-y-0 right-0 flex max-w-[500px]"
          >
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-300"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel
                class="pb-18 pointer-events-auto w-screen max-w-md bg-white shadow-xl"
              >
                <img
                  v-if="isCapacityLoading"
                  class="loading m-auto mt-16 h-12 w-12"
                  src="@/assets/loading-v2-yellow.svg"
                  alt="loading spinner"
                />
                <div
                  v-else
                  class="flex h-full max-h-screen flex-col overflow-y-auto py-6"
                >
                  <DialogTitle>
                    <div class="flex items-center justify-between px-4">
                      <div class="mb-1 flex items-center gap-1">
                        <img
                          class="-ml-1 h-5 w-5 rotate-180 cursor-pointer md:hidden"
                          src="@/assets/icon-next.svg"
                          alt="close"
                          @click="close"
                        />
                        <p
                          class="text-xl font-medium text-steel-1100 hover:underline"
                        >
                          Suggested Capacity
                        </p>
                      </div>
                      <img
                        class="cursor-pointer"
                        src="@/assets/close-icon-gray.svg"
                        alt="close"
                        @click="close"
                      />
                    </div>
                  </DialogTitle>

                  <div class="flex flex-col gap-4 p-4">
                    <div
                      ref="vehicleSearchRef"
                      class="relative"
                    >
                      <p
                        class="mb-1 ml-1 text-start font-semibold text-steel-800"
                      >
                        Vehicle
                        <span
                          v-if="requireTruckNumberForPosting"
                          class="text-red-500"
                        >
                          *
                        </span>
                      </p>
                      <div
                        class="relative flex h-10 w-full items-center rounded-lg border border-steel-300"
                        :class="{
                          'h-16': form.vehicle?.id,
                          'h-10': !form.vehicle?.id,
                          'cursor-pointer': !isEdit,
                        }"
                        @click="openVehicleDropdown"
                      >
                        <InputComponent
                          v-if="!form.vehicle?.id"
                          input-label="test"
                          wrapper-class="flex bg-white w-full text-sm items-center h-10 gap-1.5 px-2"
                          placeholder="Choose Vehicle"
                          :default-input-value="form.vehicle_query"
                          :value="form.vehicle_query"
                          @input-change="handleVehicleSearchInput"
                        />

                        <div
                          v-else
                          class="flex w-full items-center justify-between px-4 py-2 pr-10"
                        >
                          <div class="flex items-start gap-2">
                            <img
                              class="mt-1 h-5 w-5"
                              src="@/assets/loads/green-wifi.svg"
                              alt="wifi"
                            />

                            <div class="text-left">
                              <div class="flex justify-between">
                                <div class="text-left">
                                  <p class="text-sm text-steel-1100">
                                    {{
                                      form?.vehicle?.truck_number ||
                                      form?.vehicle.id
                                    }}
                                  </p>
                                </div>
                                <div
                                  v-if="form?.vehicle?.license_plate_number"
                                  class="ml-2 text-right"
                                >
                                  <p class="text-xs text-steel-700">
                                    Plate #:
                                    {{ form?.vehicle.license_plate_number }}
                                    <span
                                      v-if="form?.vehicle?.license_plate_state"
                                    >
                                      ({{ form?.vehicle.license_plate_state }})
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <p class="text-xs text-steel-700">
                                {{ form?.vehicle.vehicle?.vin }}
                              </p>
                            </div>
                          </div>
                        </div>

                        <img
                          v-if="form.vehicle?.id && !isEdit"
                          class="absolute right-4 h-2.5 opacity-60 hover:opacity-40"
                          src="@/assets/close-icon.svg"
                          alt="close"
                          @click="clearVehicle"
                        />
                      </div>

                      <div
                        v-if="showVehicleDropdown"
                        class="absolute z-20 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
                        :class="{
                          'top-[95px]': form.vehicle?.id,
                          'top-[70px]': !form.vehicle?.id,
                        }"
                      >
                        <div v-if="isCarrierVehiclesLoading">
                          <img
                            class="loading m-auto my-4 h-8 w-8"
                            src="@/assets/loading-v2-yellow.svg"
                            alt="loading"
                          />
                        </div>
                        <div v-else-if="carrier_vehicles?.length > 0">
                          <div
                            v-for="vehicle in carrier_vehicles"
                            :key="vehicle.id"
                            class="flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-steel-100"
                            @click="handleSelectVehicle(vehicle)"
                          >
                            <div class="flex items-start gap-2">
                              <img
                                class="mt-1 h-5 w-5"
                                src="@/assets/loads/green-wifi.svg"
                                alt="wifi"
                              />

                              <div class="text-left">
                                <div class="flex justify-between">
                                  <div class="text-left">
                                    <p class="text-sm text-steel-1100">
                                      {{ vehicle?.truck_number || vehicle.id }}
                                    </p>
                                  </div>
                                  <div
                                    v-if="vehicle?.license_plate_number"
                                    class="ml-2 text-right"
                                  >
                                    <p class="text-xs text-steel-700">
                                      Plate #:
                                      {{ vehicle.license_plate_number }}
                                      <span v-if="vehicle?.license_plate_state">
                                        ({{ vehicle.license_plate_state }})
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                <p class="text-xs text-steel-700">
                                  {{ vehicle.vehicle?.vin }}
                                </p>
                              </div>
                            </div>
                          </div>

                          <InfiniteScroll
                            @load-more="loadMoreDetails"
                          ></InfiniteScroll>

                          <img
                            v-if="isCarrierVehiclesLoading"
                            class="loading m-auto"
                            src="@/assets/loading-v2-yellow.svg"
                            alt="loading spinner"
                          />
                        </div>

                        <div
                          v-else
                          class="p-3"
                        >
                          <p class="text-steel-700">
                            {{
                              form.vehicle_query?.length > 0
                                ? 'No matching capacities.'
                                : 'There are no Capacities to post.'
                            }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p
                        class="mb-1 ml-1 text-start font-semibold text-steel-800"
                      >
                        Equipment
                        <span class="text-red-500">*</span>
                      </p>
                      <MultiSelectDropdown
                        v-model="form.equipment"
                        :options="equipment_options"
                        placeholder="Choose Equipment"
                        :single-option="true"
                        :is-searchable="true"
                        :input-search="equipmentSearch"
                        @input-change="handleEquipmentSearchInput"
                      ></MultiSelectDropdown>
                    </div>

                    <div class="w-full">
                      <div
                        class="relative mb-2 ml-1 flex justify-between text-start text-xs font-semibold text-steel-800"
                      >
                        <p class="ml-1 w-9/12 md:w-10/12">Origin</p>
                        <p class="ml-4 w-3/12 md:w-2/12">DH</p>
                      </div>

                      <div class="flex items-center justify-between">
                        <p
                          class="flex h-[42px] w-9/12 items-center rounded-l-lg border border-r-0 border-steel-300 p-2 pl-2 text-steel-800 hover:cursor-not-allowed md:w-10/12"
                        >
                          {{ form.origin }}
                        </p>

                        <div
                          class="capacity-dropdown -mt-[1px] h-[42px] w-3/12 !rounded-l-none rounded-r-lg border border-steel-300 md:w-2/12"
                        >
                          <dropdown-select
                            :options="deadheadOptions"
                            :default="
                              form?.origin_deadhead_tolerance?.toString() ||
                              '150'
                            "
                            :value="
                              form?.origin_deadhead_tolerance?.toString() ||
                              '150'
                            "
                            :exclude-selected="true"
                            class="deadhead-dropdown !my-0"
                            @input="handleSelectDeadheadOrigin"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="w-full">
                      <div
                        class="relative mb-1 ml-1 flex justify-between text-start text-xs font-semibold text-steel-800"
                      >
                        <p class="ml-1 w-9/12 md:w-10/12">Destination</p>

                        <p class="ml-4 w-3/12 md:w-2/12">DH</p>
                      </div>
                      <div class="flex">
                        <InputComponent
                          input-label="destination"
                          wrapper-class="capacity-location-input flex w-9/12 md:w-10/12 bg-white text-sm items-center h-10 gap-1.5 px-2 !rounded-r-none"
                          placeholder="Choose a Destination"
                          :default-input-value="form.destination"
                          :value="form.destination"
                          @input-change="handleDestinationInput"
                        />
                        <div
                          class="capacity-dropdown -mt-[1px] h-[42px] w-3/12 !rounded-l-none rounded-r-lg border border-steel-300 md:w-2/12"
                        >
                          <dropdown-select
                            :options="deadheadOptions"
                            :default="
                              form?.destination_deadhead_tolerance?.toString() ||
                              '150'
                            "
                            :value="
                              form?.destination_deadhead_tolerance?.toString() ||
                              '150'
                            "
                            :exclude-selected="true"
                            class="deadhead-dropdown !my-0"
                            @input="handleSelectDeadheadDestination"
                          />
                        </div>
                      </div>

                      <div
                        v-if="
                          form.destination?.length > 2 &&
                          isDestinationAutocompleteLoading
                        "
                        class="absolute z-50 mt-1 max-h-60 w-[calc(100%-40px)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                      >
                        <img
                          class="loading m-auto my-4 h-12 w-12"
                          src="@/assets/loading-v2-yellow.svg"
                          alt="loading"
                        />
                      </div>
                      <ul
                        v-else-if="
                          form.destination?.length > 2 &&
                          destination?.autocompleteResults?.length > 0
                        "
                        class="absolute z-50 mt-1 max-h-60 w-[calc(100%-40px)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                        tabindex="-1"
                        role="listbox"
                        aria-labelledby="listbox-label"
                        aria-activedescendant="listbox-option-3"
                      >
                        <li
                          v-for="(
                            item, index
                          ) in destination?.autocompleteResults"
                          :key="`destination-dropdown-value-${index}`"
                          class="group relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-yellow-500 hover:text-white"
                          role="option"
                        >
                          <span
                            class="flex h-6 items-center truncate font-medium"
                            @click="
                              handleAutocompleteResult('destination', item)
                            "
                          >
                            {{ item?.description }}
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div class="relative">
                      <div class="flex gap-4">
                        <p
                          class="mb-1 ml-1 flex-1 text-start font-semibold text-steel-800"
                        >
                          Start Date
                        </p>
                      </div>

                      <div class="flex w-full gap-4">
                        <div class="relative w-full">
                          <InputComponent
                            input-label="start_date"
                            wrapper-class="flex w-full text-sm items-center h-10 gap-1.5 px-2 cursor-auto capacity-date-wrapper"
                            placeholder=""
                            :default-input-value="form.start_date"
                            :value="form.start_date"
                            :show-clear-button="false"
                            :readonly="true"
                            @input-change="handleInputChange"
                            @input-click="toggleStartDatePicker"
                          />

                          <img
                            src="@/assets/calendar-light.svg"
                            class="absolute right-2 top-2 cursor-pointer opacity-90"
                            alt="calendar"
                            @click="toggleStartDatePicker"
                          />
                        </div>
                      </div>

                      <div
                        v-show="showStartDatePicker"
                        ref="startCalendarRef"
                        class="absolute bottom-12 left-0 z-50 rounded-lg bg-white p-4 shadow-md"
                      >
                        <date-picker
                          :initial-date="initialStartDate"
                          :field="'start_date'"
                          :handle-input="setSelectedStartDay"
                          date-format="MM/DD/YYYY"
                          :restrict-previous="true"
                          @day-selected="setSelectedStartDay"
                        ></date-picker>
                      </div>
                    </div>
                  </div>

                  <div
                    class="absolute bottom-0 left-0 z-20 flex w-full flex-col justify-end gap-3 border-t border-steel-300 bg-white p-4 md:flex-row"
                  >
                    <div class="flex gap-3">
                      <div
                        class="flex w-full gap-3 [&>*]:flex-1 md:[&>*]:flex-initial"
                      >
                        <ButtonComponent
                          class="!min-w-[80px] border border-steel-300"
                          @click="$emit('close')"
                        >
                          Dismiss
                        </ButtonComponent>
                        <ButtonComponent
                          variant="primary"
                          class="!min-w-[80px]"
                          :is-loading="isEditCapacityLoading"
                          :disabled="
                            isEditCapacityLoading || !hasCapacityChanged
                          "
                          @click="() => handleEditCapacity()"
                        >
                          Save
                        </ButtonComponent>
                        <ButtonComponent
                          variant="secondary"
                          class="!min-w-[130px]"
                          :is-loading="isPostCapacityLoading"
                          :disabled="isPostCapacityLoading || !canPostCapacity"
                          @click="handlePostCapacity"
                        >
                          Post Capacity
                        </ButtonComponent>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  ButtonComponent,
  InputComponent,
  InfiniteScroll,
  MultiSelectDropdown,
  LoadDropdown as DropdownSelect,
  DatePicker,
} from '@/components';
import { useClickAway } from '@/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

const props = defineProps({
  modelValue: Boolean,
  capacity: Object,
});

const emit = defineEmits(['close', 'handle-post-suggested-capacities']);

const store = useStore();
const toast = useToast();

const isOpen = ref(false);

// update this
const form = ref({
  origin: '',
  destination: '',
  origin_area_id: '',
  destination_area_id: '',
  start_date: '',
  end_date: '',
  origin_deadhead_tolerance: 150,
  destination_deadhead_tolerance: 150,
  origin_deadhead: '',
  destination_deadhead: '',
  equipment: [],
  vehicle: {},
  vehicle_query: '',
});

watchEffect(() => {
  isOpen.value = !!props.modelValue;

  form.value = {
    vehicle: props.capacity.carrier_vehicle,
    equipment: [props.capacity.posted_equipment_type],
    origin: props.capacity.origin_search_display_name,
    destination: props.capacity.destination_search_display_name,
    origin_area_id: props.capacity.origin_area_id,
    destination_area_id: props.capacity.destination_area_id,
    start_date: props.capacity.start_date,
    destination_deadhead_tolerance:
      props.capacity.destination_deadhead_tolerance || 150,
    origin_deadhead_tolerance: props.capacity.origin_deadhead_tolerance || 150,
  };
});

const carrier_id = computed(
  () =>
    store.state.users?.currentUser?.requested_carrier_id ||
    store.state.users?.currentUser?.company?.carrier_id
);

const requireTruckNumberForPosting = computed(
  () =>
    store.state.users.featureFlags?.fe_require_truck_number_for_posting?.enabled
);

const destination = computed(() => store.state.laneSearches.destination);
const isDestinationAutocompleteLoading = computed(
  () => store.state.laneSearches.isDestinationAutocompleteLoading
);

const handleDestinationInput = debounce(async (value) => {
  if (value.length > 2) {
    form.value.destination = value;
    await store.dispatch('laneSearches/getAutocomplete', {
      isType: 'destination',
      term: value,
    });
  } else if (value.length === 0) {
    form.value.destination = '';
    form.value.destination_area_id = '';
    await store.dispatch('laneSearches/getAutocomplete', {
      isType: 'destination',
      term: value,
    });
  }
}, 500);

const handleAutocompleteResult = async (isType, item) => {
  await store.dispatch('laneSearches/updateFromGooglePlace', {
    isType: isType,
    googlePlace: item,
  });
  switch (isType) {
    case 'origin':
      store.commit('laneSearches/updateOrigin', {
        autocompleteResults: [],
        showBadge: true,
      });
      break;
    case 'destination':
      store.commit('laneSearches/updateDestination', {
        autocompleteResults: [],
        showBadge: true,
      });
      break;
  }
  const areaInformation = await store.dispatch(
    'laneSearches/updateAreaIdFromGooglePlace',
    isType
  );
  if (areaInformation?.data) {
    if (isType === 'origin') {
      form.value.origin_area_id = areaInformation?.data?.id;
      form.value.origin = item?.description;
    } else {
      form.value.destination_area_id = areaInformation?.data?.id;
      form.value.destination = item?.description;
    }
  }
};

const deadheadOptions = ref([
  '50',
  '100',
  '150',
  '200',
  '250',
  '300',
  '350',
  '400',
  '450',
  '500',
]);

const handleSelectDeadheadDestination = (item) => {
  form.value.destination_deadhead_tolerance = item;
};

const handleSelectDeadheadOrigin = (item) => {
  form.value.origin_deadhead_tolerance = item;
};

const equipment_types = computed(() => {
  const raw = store.state.loads?.equipment_types || {};

  return Object.fromEntries(
    Object.entries(raw).filter(([key]) => key.toLowerCase() !== 'other')
  );
});

const equipmentSearch = ref('');

const handleEquipmentSearchInput = (e) => {
  if (e?.target?.value) {
    equipmentSearch.value = e.target.value.toLowerCase();
  } else {
    equipmentSearch.value = '';
  }
};

const equipment_options = computed(() => {
  return Object.entries(equipment_types?.value)
    ?.map(([value, key]) => {
      return { label: key, value };
    })
    .filter((option) => {
      return option.label.toLowerCase().includes(equipmentSearch.value);
    });
});

const isCarrierVehiclesLoading = computed(
  () => store.state.loads?.is_carrier_vehicles_loading
);

const vehiclesPagination = computed(
  () => store.state.loads?.eld_carrier_vehicles_pagination
);

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const loadMoreDetails = async () => {
  if (!isCarrierVehiclesLoading.value) {
    console.log('Hit infinite scroll');

    if (
      vehiclesPagination?.value?.totalRecords !==
      carrier_vehicles?.value?.length
    ) {
      await store.dispatch('loads/getCarrierVehicles', {
        id: carrier_id.value,
        page: vehiclesPagination?.value?.currentPage + 1,
        refetch: true,
      });
    }
  }
};

const showVehicleDropdown = ref(false);

const openVehicleDropdown = () => {
  showVehicleDropdown.value = true;
};

const toggleVehicleDropdown = () => {
  if (!props.isEdit) {
    showVehicleDropdown.value = !showVehicleDropdown.value;
  }
};

const handleSelectVehicle = (vehicle) => {
  form.value.vehicle = vehicle;
  toggleVehicleDropdown();
};

const handleVehicleSearchInput = debounce(async (value) => {
  form.value.vehicle_query = value;
  showVehicleDropdown.value = true;

  await store.dispatch('loads/getCarrierVehicles', {
    id: carrier_id.value,
    query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc&q[truck_number_or_license_plate_number_or_vehicle_vin_start]=${value}`,
  });
}, 500);

const clearVehicle = async () => {
  form.value.vehicle_query = '';
  form.value.vehicle = {};

  setTimeout(() => {
    showVehicleDropdown.value = false;
  }, 0);

  await store.dispatch('loads/getCarrierVehicles', {
    id: carrier_id.value,
    query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc`,
  });
};

const close = () => {
  isOpen.value = false;
  emit('update:modelValue', false);
};
const isCapacityLoading = computed(() => false);

const canPostCapacity = computed(() => {
  return (
    form.value.origin_area_id &&
    form.value.equipment?.length > 0 &&
    form.value.start_date &&
    (requireTruckNumberForPosting.value
      ? form.value.vehicle?.truck_number
      : true)
  );
});

const isEditCapacityLoading = ref(false);

const handleEditCapacity = async (edit_and_post = false) => {
  isEditCapacityLoading.value = true;

  const res = await store.dispatch('loads/updatePotentialTruckPosting', {
    id: props.capacity?.id,
    carrier_vehicle_id: form.value.vehicle?.id || '',
    destination_area_id: form.value.destination_area_id || '',
    start_date: dayjs(form.value.start_date).format('YYYY-MM-DD'),
    posted_equipment_type: form.value.equipment[0],
    origin_deadhead_tolerance: form.value.origin_deadhead_tolerance,
    destination_deadhead_tolerance: form.value.destination_deadhead_tolerance,
    destination_search_display_name: form.value.destination,
    is_active: false,
  });

  if ([200, 201, 204].includes(res?.status) && res.data) {
    isEditCapacityLoading.value = false;

    if (!edit_and_post) {
      toast.open({
        message: 'Capacity successfully updated.',
        position: 'top-right',
        duration: 5000,
      });
      emit('close');
      await store.dispatch('loads/getPotentialTruckPostings', {});
    }
  } else if ([422].includes(res?.status)) {
    // if out of range / error
    isEditCapacityLoading.value = false;

    emit('close');

    toast.open({
      message: 'Unable to update capacity. Please try again.',
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  } else {
    // close with no specific error
    isEditCapacityLoading.value = false;

    emit('close');

    toast.open({
      message: 'Unexpected error occured',
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const hasCapacityChanged = computed(() => {
  return (
    form.value.origin !== props.capacity.origin_search_display_name ||
    (form.value.destination_area_id &&
      form.value.destination !==
        props.capacity.destination_search_display_name) ||
    (form.value.equipment?.length > 0 &&
      form.value.equipment[0] !== props.capacity.posted_equipment_type) ||
    form.value.vehicle?.id !== props.capacity.carrier_vehicle?.id ||
    form.value.start_date !== props.capacity.start_date
  );
});

const isPostCapacityLoading = ref(false);
const handlePostCapacity = async () => {
  if (hasCapacityChanged.value) {
    await handleEditCapacity(true);
  }

  isPostCapacityLoading.value = true;
  await emit('handle-post-suggested-capacities', [props.capacity?.id]);

  setTimeout(() => {
    emit('close');
    isPostCapacityLoading.value = false;
  }, 500);
};

const showStartDatePicker = ref(false);

const toggleStartDatePicker = () => {
  showStartDatePicker.value = !showStartDatePicker.value;
};

const initialStartDate = computed(() => {
  return dayjs().format('MM/DD/YYYY');
});

const setSelectedStartDay = (fieldValue, dateSelected) => {
  if (dateSelected) {
    form.value.start_date = dayjs(dateSelected).format('MM/DD/YYYY');

    showStartDatePicker.value = false;
  }
};

const startCalendarRef = ref();
useClickAway(startCalendarRef, () => {
  showStartDatePicker.value = false;
});

const vehicleSearchRef = ref();
useClickAway(vehicleSearchRef, () => {
  showVehicleDropdown.value = false;
});
</script>
